// ==============================================
// Admin Fleet Detail Page
// ==============================================

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  PlusIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { DeviceStatus } from '../../types/device';

const statusColors: Record<DeviceStatus, string> = {
  unregistered: 'bg-slate-500/20 text-slate-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  active: 'bg-green-500/20 text-green-400',
  suspended: 'bg-orange-500/20 text-orange-400',
  revoked: 'bg-red-500/20 text-red-400',
};

const statusIcons: Record<DeviceStatus, React.ReactNode> = {
  unregistered: <ArchiveBoxIcon className="w-4 h-4" />,
  pending: <ClockIcon className="w-4 h-4" />,
  active: <CheckCircleIcon className="w-4 h-4" />,
  suspended: <XCircleIcon className="w-4 h-4" />,
  revoked: <XCircleIcon className="w-4 h-4" />,
};

const GROUP_COLORS = [
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Yellow', value: '#eab308' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Cyan', value: '#06b6d4' },
  { label: 'Pink', value: '#ec4899' },
];

export function AdminFleetDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Fetch organization details
  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['admin', 'organization', orgId],
    queryFn: () => api.adminGetOrganization(orgId!),
    enabled: !!orgId,
  });

  // Fetch organization devices
  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ['admin', 'organization', orgId, 'devices'],
    queryFn: () => api.adminGetOrganizationDevices(orgId!),
    enabled: !!orgId,
  });

  // Fetch fleet health stats
  const { data: health } = useQuery({
    queryKey: ['admin', 'organization', orgId, 'health'],
    queryFn: () => api.adminGetFleetHealth(orgId!),
    enabled: !!orgId,
  });

  // Fetch device groups
  const { data: groups } = useQuery({
    queryKey: ['admin', 'organization', orgId, 'groups'],
    queryFn: () => api.adminListDeviceGroups(orgId!),
    enabled: !!orgId,
  });

  // Bulk command mutation
  const bulkCommandMutation = useMutation({
    mutationFn: ({ command, params }: { command: string; params?: any }) =>
      api.adminBulkCommand(orgId!, command, params),
    onSuccess: (_data, variables) => {
      alert(`Bulk command "${variables.command}" sent successfully.`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'organization', orgId] });
    },
    onError: (error: any) => {
      alert(`Bulk command failed: ${error.response?.data?.error || error.message}`);
    },
  });

  const handleBulkCommand = (command: string) => {
    if (confirm(`Are you sure you want to send "${command}" to all devices in this organization?`)) {
      bulkCommandMutation.mutate({ command });
    }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Compute stats from devices or health data
  const totalDevices = health?.total_devices ?? devices?.length ?? 0;
  const onlineDevices = health?.online ?? 0;
  const offlineDevices = health?.offline ?? 0;
  const lowDiskDevices = health?.low_disk ?? 0;

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400 text-lg">Organization not found</p>
        <button
          onClick={() => navigate('/admin/fleets')}
          className="mt-4 text-cyan-400 hover:text-cyan-300"
        >
          Back to Fleet Management
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/fleets')}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <BuildingOffice2Icon className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{org.name}</h1>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <span className="font-mono text-xs">{org.slug}</span>
                {org.contact_email && (
                  <>
                    <span className="text-slate-600">|</span>
                    <span className="flex items-center gap-1">
                      <EnvelopeIcon className="w-3.5 h-3.5" />
                      {org.contact_email}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Command Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleBulkCommand('sync_all')}
            disabled={bulkCommandMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Sync All
          </button>
          <button
            onClick={() => handleBulkCommand('push_config')}
            disabled={bulkCommandMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
          >
            <Cog6ToothIcon className="w-4 h-4" />
            Push Config
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Devices"
          value={totalDevices}
          icon={<DevicePhoneMobileIcon className="w-5 h-5" />}
        />
        <StatCard
          label="Online"
          value={onlineDevices}
          icon={<CheckCircleIcon className="w-5 h-5" />}
          color="text-green-400"
        />
        <StatCard
          label="Offline"
          value={offlineDevices}
          icon={<XCircleIcon className="w-5 h-5" />}
          color="text-slate-400"
        />
        <StatCard
          label="Low Disk"
          value={lowDiskDevices}
          icon={<ExclamationTriangleIcon className="w-5 h-5" />}
          color="text-yellow-400"
        />
      </div>

      {/* Device Groups Section */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Device Groups</h2>
          <button
            onClick={() => setShowCreateGroupModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Create Group
          </button>
        </div>

        {!groups || groups.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-400">
            <p>No device groups yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Create groups to organize devices within this fleet.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {groups.map((group: any) => {
              const isExpanded = expandedGroups.has(group.id);
              const groupDevices = devices?.filter(
                (d: any) => d.group_id === group.id
              );
              return (
                <div key={group.id}>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center gap-3 px-6 py-3 hover:bg-slate-800/30 transition-colors text-left"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                    )}
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: group.color || '#3b82f6' }}
                    />
                    <span className="font-medium text-white">{group.name}</span>
                    {group.description && (
                      <span className="text-sm text-slate-500 truncate">
                        - {group.description}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-slate-500">
                      {groupDevices?.length ?? 0} device{(groupDevices?.length ?? 0) !== 1 ? 's' : ''}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-3 pl-14">
                      {!groupDevices || groupDevices.length === 0 ? (
                        <p className="text-sm text-slate-500 py-2">
                          No devices in this group
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {groupDevices.map((device: any) => (
                            <div
                              key={device.id}
                              onClick={() => navigate(`/admin/devices/${device.id}`)}
                              className="flex items-center justify-between bg-slate-900/50 rounded-lg px-4 py-2 border border-slate-700/50 cursor-pointer hover:border-slate-600 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                {device.device_type === 'scout' ? (
                                  <DevicePhoneMobileIcon className="w-4 h-4 text-cyan-400" />
                                ) : (
                                  <ServerIcon className="w-4 h-4 text-purple-400" />
                                )}
                                <span className="text-sm text-white">
                                  {device.name || device.serial_number}
                                </span>
                              </div>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                                  statusColors[device.status as DeviceStatus] || 'bg-slate-500/20 text-slate-400'
                                }`}
                              >
                                {statusIcons[device.status as DeviceStatus]}
                                {device.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Devices Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">All Devices</h2>
        </div>

        {devicesLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !devices || devices.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-400">
            No devices assigned to this organization
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-slate-400 border-b border-slate-700 bg-slate-800/50">
                <th className="px-6 py-3 font-medium">Device</th>
                <th className="px-6 py-3 font-medium">Type</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Connection Code</th>
                <th className="px-6 py-3 font-medium">Owner</th>
                <th className="px-6 py-3 font-medium">Last Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {devices.map((device: any) => (
                <tr
                  key={device.id}
                  className="hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => navigate(`/admin/devices/${device.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        {device.device_type === 'scout' ? (
                          <DevicePhoneMobileIcon className="w-5 h-5 text-cyan-400" />
                        ) : (
                          <ServerIcon className="w-5 h-5 text-purple-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {device.name || device.serial_number}
                        </p>
                        <p className="text-sm text-slate-400">{device.serial_number}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-300">
                      {device.device_type === 'scout' ? 'Scout' : 'Command Hub'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                        statusColors[device.status as DeviceStatus] || 'bg-slate-500/20 text-slate-400'
                      }`}
                    >
                      {statusIcons[device.status as DeviceStatus]}
                      {device.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {device.registration_code ? (
                      <div className="flex items-center gap-1">
                        <code className={`text-sm font-mono px-2 py-0.5 rounded ${
                          device.registration_code_expires_at && new Date(device.registration_code_expires_at) < new Date()
                            ? 'text-red-400 bg-red-500/10'
                            : 'text-cyan-400 bg-cyan-500/10'
                        }`}>
                          {device.registration_code}
                        </code>
                        {device.registration_code_expires_at && new Date(device.registration_code_expires_at) < new Date() && (
                          <span className="text-xs text-red-400 font-medium">Expired</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {device.user ? (
                      <div>
                        <p className="text-sm text-white">{device.user.name || 'Unnamed'}</p>
                        <p className="text-xs text-slate-400">{device.user.email}</p>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500">Unassigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-400">
                    {device.last_seen_at
                      ? new Date(device.last_seen_at).toLocaleString('en-NZ')
                      : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <CreateGroupModal
          orgId={orgId!}
          onClose={() => setShowCreateGroupModal(false)}
          onCreated={() => {
            queryClient.invalidateQueries({
              queryKey: ['admin', 'organization', orgId, 'groups'],
            });
            setShowCreateGroupModal(false);
          }}
        />
      )}
    </div>
  );
}

// ==========================================
// Stat Card Component
// ==========================================

function StatCard({
  label,
  value,
  icon,
  color = 'text-white',
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color?: string;
}) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400">{icon}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
}

// ==========================================
// Create Group Modal
// ==========================================

function CreateGroupModal({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      api.adminCreateDeviceGroup(orgId, {
        name: data.name,
        description: data.description || undefined,
        color: data.color,
      }),
    onSuccess: () => {
      onCreated();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4">Create Device Group</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Group Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              placeholder="e.g., North Fleet"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional description"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {GROUP_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: c.value })}
                  className={`w-8 h-8 rounded-lg border-2 transition-colors ${
                    formData.color === c.value
                      ? 'border-white scale-110'
                      : 'border-transparent hover:border-slate-500'
                  }`}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          {createMutation.isError && (
            <p className="text-red-400 text-sm">
              {(createMutation.error as any)?.response?.data?.error ||
                'Failed to create group'}
            </p>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !formData.name}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminFleetDetailPage;
